import useImpressionTracking, {
  useImpressionTrackingProps,
} from 'hooks/gtm/use-impression-tracking';
import { Gtm } from 'tracking';
import {
  Children,
  cloneElement,
  FC,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { slugify, toCamelCase } from 'utils/strings';

export interface ImpressionTrackerProps {
  /** The name of the tracked element, e.g. ["Mein Hund Ist sehr waehlerisch"] */
  element: string | string[];
  /** The name of the list that the tracked element belongs to */
  list?: string;
  /** useImpressionTrackingProps */
  options?: useImpressionTrackingProps['options'];
  /** Custom event */
  customEvent?: () => void;
}

/** Tracks the impression of its single child, whether that child's node mounts on the first commit or a later one. */
const ImpressionTrackerNew: FC<PropsWithChildren<ImpressionTrackerProps>> = ({
  children,
  element,
  list,
  options = { threshold: 0.9 },
  customEvent,
}) => {
  const ref = useRef<HTMLElement | null>(null);
  // Value is never read: writing a ref doesn't re-render but `setNode` does, which makes
  // useIntersection re-read `ref.current` in its effect deps when the node attaches late.
  const [, setNode] = useState<HTMLElement | null>(null);

  const setRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
    setNode(node);
  }, []);

  const impressionId = useMemo(
    () => (typeof element === 'string' ? element : element.join('#')),
    [element]
  );

  useImpressionTracking({
    impressionStoreKey:
      typeof element === 'string' ? toCamelCase(element) : toCamelCase(element[0]),
    impressionId: slugify(impressionId),
    trackingFn: (isInViewport: boolean) => {
      if (isInViewport) {
        customEvent ? customEvent() : Gtm.track.viewElement({ element, list });
      }
    },
    ref,
    options,
  });

  const child = Children.only(children) as ReactElement;

  return cloneElement(child, { ref: setRef });
};

export default ImpressionTrackerNew;
